import { Component, h, Host, Element, Prop } from '@stencil/core';

/**
 * Vertical feature card with icon, heading, and description, rendered as a host element with Schema.org microdata.
 * When `href` is provided the entire card becomes a clickable link.
 * No action link by default — informational only. Alternative to features-card-type-2 without actions;
 * suitable for icon-led grids without a card CTA.
 *
 * @category Features
 * @slot icon - {required}, <me-icon>, <me-picture>
 * @slot heading - {required}, <me-heading>
 * @slot description - {required}, <me-text>
 * @example
 *   <features-card-type-6 href="/articles/signing">
 *     <me-icon slot="icon" name="interface--shield"></me-icon>
 *     <me-heading slot="heading" variant="5">Enterprise Security</me-heading>
 *     <me-text slot="description">Bank-level encryption keeps your data safe and secure.</me-text>
 *   </features-card-type-6>
 */

@Component({
  tag: 'features-card-type-6',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FeaturesCardType6 {
  @Element() host: HTMLElement;

  /**
   * Optional URL. When set the entire card renders as a clickable `<a>` link.
   * @content
   */
  @Prop() href?: string;

  render() {
    // Get position from host attributes if present (for microdata)
    const positionAttr = this.host.getAttribute('position');
    const hasItemscope = this.host.hasAttribute('itemscope');
    const itemtypeAttr = this.host.getAttribute('itemtype');
    const itempropAttr = this.host.getAttribute('itemprop');

    const inner = [
      <div class="features-card-type-6__icon">
        <slot name="icon"></slot>
      </div>,
      <slot name="heading"></slot>,
      <slot name="description"></slot>,
      hasItemscope && positionAttr ? <meta itemProp="position" content={positionAttr} /> : null,
    ];

    return (
      <Host class="features-card-type-6" itemScope={hasItemscope || undefined} itemType={itemtypeAttr || undefined} itemProp={itempropAttr || undefined}>
        {this.href
          ? <a class="features-card-type-6__link" href={this.href}>{inner}</a>
          : inner
        }
      </Host>
    );
  }
}
